import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeader from '@/components/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import ProvinciaSelect from '@/components/geo/ProvinciaSelect.jsx';
import LocalidadSelect from '@/components/geo/LocalidadSelect.jsx';
import {
  createEmpresaRedespacho,
  deleteEmpresaRedespacho,
  getApiErrorMessage,
  getEmpresaRedespacho,
  listEmpresasRedespacho,
  updateEmpresaRedespacho,
} from '@/services/sucursalesOperativas.js';

const inputClassName = 'h-10 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:bg-[rgba(var(--subpanel),0.75)]';
const panelClassName = 'rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.92)] p-3 shadow-[0_10px_30px_rgba(var(--shadow),0.12)] md:p-4';

const emptyForm = {
  codigo: '',
  nombre: '',
  razon_social: '',
  cuit: '',
  telefono: '',
  email: '',
  domicilio: '',
  provincia_id: '',
  localidad_id: '',
  contacto: '',
  observaciones: '',
  activo: true,
};

function formatCuit(value) {
  const digits = String(value || '').replace(/[^\d]/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

export default function EmpresasRedespachoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: '', activa: '1' });
  const [form, setForm] = useState(emptyForm);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRows = async () => {
    setLoadingRows(true);
    try {
      const data = await listEmpresasRedespacho({
        q: filters.q || undefined,
        activa: filters.activa,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      setRows([]);
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudieron cargar las empresas de redespacho'), 'error');
    } finally {
      setLoadingRows(false);
    }
  };

  const loadDetail = async (targetId) => {
    if (!targetId) {
      setForm(emptyForm);
      return;
    }

    setLoadingDetail(true);
    try {
      const data = await getEmpresaRedespacho(targetId);
      setForm({
        codigo: data?.codigo || '',
        nombre: data?.nombre || '',
        razon_social: data?.razon_social || '',
        cuit: data?.cuit || '',
        telefono: data?.telefono || '',
        email: data?.email || '',
        domicilio: data?.domicilio || '',
        provincia_id: data?.provincia_id ? String(data.provincia_id) : '',
        localidad_id: data?.localidad_id ? String(data.localidad_id) : '',
        contacto: data?.contacto || '',
        observaciones: data?.observaciones || '',
        activo: Boolean(data?.activo ?? true),
      });
    } catch (error) {
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudo cargar la empresa de redespacho'), 'error');
      navigate('/administracion/redespachos', { replace: true });
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [filters.q, filters.activa]);

  useEffect(() => {
    loadDetail(id);
  }, [id]);

  const filteredRows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.codigo, row.nombre, row.razon_social, row.cuit, row.localidad_nombre, row.provincia_nombre]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [rows, filters.q]);

  const validateForm = () => {
    const errors = [];
    if (!form.codigo.trim()) errors.push('El código es obligatorio.');
    if (!form.nombre.trim()) errors.push('El nombre es obligatorio.');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.push('El email es inválido.');

    if (errors.length) {
      Swal.fire('Atención', errors.join(' '), 'warning');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        codigo: form.codigo.trim(),
        nombre: form.nombre.trim(),
        razon_social: form.razon_social.trim(),
        cuit: form.cuit.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim(),
        domicilio: form.domicilio.trim(),
        provincia_id: form.provincia_id ? Number(form.provincia_id) : null,
        localidad_id: form.localidad_id ? Number(form.localidad_id) : null,
        contacto: form.contacto.trim(),
        observaciones: form.observaciones.trim(),
        activo: Boolean(form.activo),
      };

      if (isEditing) {
        await updateEmpresaRedespacho(id, payload);
      } else {
        await createEmpresaRedespacho(payload);
      }

      await loadRows();
      await Swal.fire('Listo', isEditing ? 'Empresa actualizada correctamente' : 'Empresa creada correctamente', 'success');
      navigate('/administracion/redespachos', { replace: true });
    } catch (error) {
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudo guardar la empresa de redespacho'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row) => {
    const decision = await Swal.fire({
      title: row.activo ? '¿Desactivar empresa?' : 'La empresa ya está inactiva',
      text: row.activo ? 'Se bloqueará para nuevas parametrizaciones de localidades.' : '',
      icon: row.activo ? 'question' : 'info',
      showCancelButton: row.activo,
      confirmButtonText: 'Desactivar',
    });
    if (!decision.isConfirmed) return;

    try {
      await deleteEmpresaRedespacho(row.id);
      await loadRows();
      if (String(id || '') === String(row.id)) {
        navigate('/administracion/redespachos', { replace: true });
      }
    } catch (error) {
      await Swal.fire('Error', getApiErrorMessage(error, 'No se pudo desactivar la empresa'), 'error');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 px-2 py-2 md:px-3 md:py-3">
      <PageHeader
        title="Empresas de redespacho"
        subtitle=""
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link className="inline-flex h-9 items-center rounded-xl border border-[rgb(var(--border))] px-3 text-sm font-medium text-[rgb(var(--fg))]" to="/administracion/empresa/localidades-atendidas">
              Localidades atendidas
            </Link>
            <Link className="inline-flex h-9 items-center rounded-xl bg-[rgb(var(--brand))] px-3 text-sm font-semibold text-white" to="/administracion/redespachos/nuevo">
              Nueva empresa
            </Link>
          </div>
        }
      />

      <section className={`${panelClassName} grid grid-cols-12 gap-3`}>
        <div className="col-span-12 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">
              {isEditing ? `Editar redespacho #${id}` : 'Alta de empresa de redespacho'}
            </h2>
            <p className="text-xs text-[rgba(var(--muted),0.88)]">
              Maestro operativo del tercero que realiza el tramo o la entrega final por afuera de la sucursal cabecera.
            </p>
          </div>
        </div>

        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Código</label>
          <input className={inputClassName} value={form.codigo} onChange={(e) => setForm((current) => ({ ...current, codigo: e.target.value.toUpperCase() }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-4">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Nombre</label>
          <input className={inputClassName} value={form.nombre} onChange={(e) => setForm((current) => ({ ...current, nombre: e.target.value }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-5">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Razón social</label>
          <input className={inputClassName} value={form.razon_social} onChange={(e) => setForm((current) => ({ ...current, razon_social: e.target.value }))} disabled={loadingDetail} />
        </div>

        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">CUIT</label>
          <input className={inputClassName} value={form.cuit} onChange={(e) => setForm((current) => ({ ...current, cuit: formatCuit(e.target.value) }))} disabled={loadingDetail} placeholder="##-########-#" />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Teléfono</label>
          <input className={inputClassName} value={form.telefono} onChange={(e) => setForm((current) => ({ ...current, telefono: e.target.value }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Email</label>
          <input className={inputClassName} value={form.email} onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Contacto</label>
          <input className={inputClassName} value={form.contacto} onChange={(e) => setForm((current) => ({ ...current, contacto: e.target.value }))} disabled={loadingDetail} />
        </div>

        <div className="col-span-12 md:col-span-6">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Domicilio</label>
          <input className={inputClassName} value={form.domicilio} onChange={(e) => setForm((current) => ({ ...current, domicilio: e.target.value }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <ProvinciaSelect
            label="Provincia"
            value={form.provincia_id}
            onChange={(value) => setForm((current) => ({ ...current, provincia_id: String(value || ''), localidad_id: '' }))}
            disabled={loadingDetail}
          />
        </div>
        <div className="col-span-12 md:col-span-3">
          <LocalidadSelect
            label="Localidad"
            provinciaId={form.provincia_id}
            value={form.localidad_id}
            onChange={(value) => setForm((current) => ({ ...current, localidad_id: String(value || '') }))}
            disabled={loadingDetail || !form.provincia_id}
          />
        </div>

        <div className="col-span-12 md:col-span-9">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Observaciones</label>
          <textarea className="min-h-[96px] w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.88)] px-3 py-2 text-sm text-[rgb(var(--fg))] outline-none transition focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" value={form.observaciones} onChange={(e) => setForm((current) => ({ ...current, observaciones: e.target.value }))} disabled={loadingDetail} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="rounded-2xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.34)] p-3">
            <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
              <input type="checkbox" checked={form.activo} onChange={(e) => setForm((current) => ({ ...current, activo: e.target.checked }))} disabled={loadingDetail} />
              <span>Empresa activa</span>
            </label>
            <p className="mt-2 text-xs text-[rgba(var(--muted),0.85)]">
              Si la desactivás, no podrá seguir seleccionándose en localidades atendidas.
            </p>
          </div>
        </div>

        <div className="col-span-12">
          <StickyActions
            onCancel={() => navigate('/administracion/redespachos')}
            onSubmit={handleSubmit}
            isSubmitting={saving}
            primaryLabel={isEditing ? 'Guardar cambios' : 'Crear empresa'}
            secondaryLabel="Volver"
          />
        </div>
      </section>

      <section className={`${panelClassName} grid grid-cols-12 gap-3`}>
        <div className="col-span-12 md:col-span-5">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Búsqueda</label>
          <input className={inputClassName} placeholder="Código, nombre, CUIT o localidad" value={filters.q} onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))} />
        </div>
        <div className="col-span-12 md:col-span-3">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.08em] text-[rgba(var(--muted),0.92)]">Estado</label>
          <select className={inputClassName} value={filters.activa} onChange={(e) => setFilters((current) => ({ ...current, activa: e.target.value }))}>
            <option value="">Todas</option>
            <option value="1">Activas</option>
            <option value="0">Inactivas</option>
          </select>
        </div>

        <div className="col-span-12">
          {loadingRows ? (
            <div className="py-8 text-center text-sm text-[rgba(var(--muted),0.88)]">Cargando empresas de redespacho...</div>
          ) : filteredRows.length === 0 ? (
            <EmptyState title="Sin empresas de redespacho" description="Cargá el primer tercero para empezar a parametrizar destinos con redespacho real." />
          ) : (
            <div className="overflow-auto rounded-2xl border border-[rgba(var(--border),0.55)]">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.58)] text-[rgba(var(--muted),0.95)]">
                  <tr>
                    <th className="px-3 py-2">Código</th>
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">CUIT</th>
                    <th className="px-3 py-2">Ubicación</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id} className="border-t border-[rgba(var(--border),0.42)]">
                      <td className="px-3 py-2 font-medium">{row.codigo}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{row.nombre}</div>
                        {row.razon_social ? <div className="text-xs text-[rgba(var(--muted),0.88)]">{row.razon_social}</div> : null}
                      </td>
                      <td className="px-3 py-2">{row.cuit || '-'}</td>
                      <td className="px-3 py-2">
                        {[row.localidad_nombre, row.provincia_nombre].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${row.activo ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.14)] text-[rgba(var(--muted),0.95)]'}`}>
                          {row.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => navigate(`/administracion/redespachos/${row.id}`)}>
                            Editar
                          </button>
                          <button type="button" className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs" onClick={() => handleDeactivate(row)}>
                            {row.activo ? 'Desactivar' : 'Inactiva'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
